import React, { useState } from 'react';
import { Loading, PrimaryAltButton } from '../atoms';
import { PlusIcon } from '../atoms/icons';
import styled, { css } from 'styled-components';
import type { TAccountProductType } from '../types';
import { ProductDescriptions } from '../types';
import {
  addProductToMerchant,
  useAccountStore,
  useProductStore
} from '../../contexts';
import { useMutation } from '@tanstack/react-query';
import { compact } from 'lodash';

const ProductList = styled.div(
  ({ theme }) => css`
    position: absolute;
    z-index: 100;
    margin: 0;
    color: ${theme.buttons.primaryAlt.background};
    background-color: ${theme.buttons.primaryAlt.color};
    max-width: 90vw;
    max-height: 88vh;
    overflow-y: auto;
    border-radius: ${theme.borderRadii.default};
    padding: 16px 24px;

    @media (max-width: ${theme.breakpoints.max.sm}) {
      padding: 12px 16px;
      max-width: 95vw;
    }
  `
);

const ProductListItem = styled.div(
  ({ theme }) => css`
    cursor: pointer;
    padding: 8px 4px;

    @media (hover: hover) {
      &:hover {
        color: ${theme.buttons.primaryAlt.backgroundHover};
        background-color: ${theme.buttons.primaryAlt.colorHover};
      }
    }
  `
);

interface AccountProductsAddListProps {
  merchantId: string;
}

export function AccountProductsAddList({
  merchantId
}: AccountProductsAddListProps) {
  const [productListOpen, setProductListOpen] = useState(false);
  const currentProducts = useAccountStore((s) => s.currentProducts);
  const currentProductsLoading = useAccountStore(
    (s) => s.currentProductsLoading
  );
  const setCurrentProductIds = useAccountStore((s) => s.setCurrentProductIds);
  const addProductToMerchantMutation = addProductToMerchant({
    merchantId,
    setCurrentProductIds,
    mutate: useMutation
  });

  const availableTypes = useProductStore((s) => s.availableTypes);
  const availableTypesLoading = useProductStore((s) => s.availableTypesLoading);

  const addableProductTypes: ReadonlyArray<TAccountProductType> =
    availableTypes.filter(({ code }) => {
      const typeHasExistingProduct = currentProducts.some(
        ({ productType }) => code === productType
      );

      return !typeHasExistingProduct;
    });

  // @TODO, just disable the button and tell them why it's disabled
  // if (!addableProductTypes?.length) return null;
  const isLoading = currentProductsLoading || availableTypesLoading;

  return (
    <>
      <PrimaryAltButton
        type="button"
        disabled={
          isLoading ||
          !addableProductTypes?.length ||
          addProductToMerchantMutation.isLoading
        }
        isLoading={isLoading || addProductToMerchantMutation.isLoading}
        onClick={() => setProductListOpen((prev) => !prev)}
      >
        Add Product <PlusIcon size="0.9em" />
      </PrimaryAltButton>
      {productListOpen && (
        <ProductList>
          {addableProductTypes?.map(({ code, description: _description }) => {
            const description =
              _description ||
              ProductDescriptions[code] ||
              ProductDescriptions[code.replace('Nim', '')] ||
              '';
            return (
              <ProductListItem
                key={code}
                onClick={() => {
                  setProductListOpen(false);
                  addProductToMerchantMutation.mutate(code);
                }}
              >
                {compact([code, description]).join(' - ')}
              </ProductListItem>
            );
          })}
        </ProductList>
      )}
    </>
  );
}
